import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/auth/AuthProvider'
import { inputClass } from '@/components/ui/Field'

type TFn = (key: string, fallback: string) => string

function friendlyError(err: unknown, t: TFn): string {
  const code = (err as { code?: string })?.code || ''
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return t('loginFailedMessage', 'Unable to log in. Please check your credentials.')
    case 'auth/invalid-email':
      return t('emailError', 'Please enter a valid email')
    case 'auth/too-many-requests':
      return t('webapp.authErrors.tooMany', 'Too many attempts. Please try again later.')
    case 'auth/account-exists-with-different-credential':
      return t('authErrors.differentCredential', 'An account already exists for this email with a different sign-in method. Try the method you used in the app — email & password, Google, or Apple.')
    case 'auth/popup-blocked':
      return t('webapp.authErrors.popupBlocked', 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.')
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return ''
    default:
      return t('webapp.log.error', 'Something went wrong. Please try again.')
  }
}

export function SignIn() {
  const { t } = useTranslation()
  const { signInEmail, signInGoogle, signInApple, resetPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  async function run(fn: () => Promise<void>) {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await fn()
    } catch (err) {
      const msg = friendlyError(err, t)
      if (msg) setError(msg)
    } finally {
      setBusy(false)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void run(() => signInEmail(email.trim(), password))
  }

  async function onForgot() {
    if (!email.trim()) {
      setError(t('enterEmailToReset', 'Enter your email above first, then tap "Forgot password".'))
      return
    }
    await run(async () => {
      await resetPassword(email.trim())
      setNotice(t('passwordResetSent', 'Password reset email sent. Check your inbox.'))
    })
  }

  return (
    <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
      <div className="w-full max-w-md animate-fade-in rounded-xl border border-border-light bg-surface p-xl shadow-lg">
        <div className="mb-lg flex flex-col items-center gap-sm">
          <img src="/icon-192.png" alt="" className="h-16 w-16 rounded-[18px]" />
          <h1 className="text-h2 font-bold text-text">Vore</h1>
          <p className="text-center text-body-sm text-text-secondary">{t('webapp.authSubtitle', 'Sign in to your Vore account')}</p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-md">
          <label className="flex flex-col gap-xs">
            <span className="text-body-sm font-medium text-text-secondary">{t('email', 'Email')}</span>
            <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
          </label>
          <label className="flex flex-col gap-xs">
            <span className="text-body-sm font-medium text-text-secondary">{t('password', 'Password')}</span>
            <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} />
          </label>

          {error ? <p className="text-body-sm text-red">{error}</p> : null}
          {notice ? <p className="text-body-sm text-green">{notice}</p> : null}

          <button type="submit" disabled={busy} className="mt-xs rounded-md bg-primary px-md py-smd text-button font-semibold text-on-primary transition-opacity hover:opacity-90 disabled:opacity-60">
            {t('logIn', 'Sign In')}
          </button>
          <button type="button" onClick={() => void onForgot()} disabled={busy} className="text-body-sm text-primary hover:underline">
            {t('forgotPassword', 'Forgot password?')}
          </button>
        </form>

        <div className="my-lg flex items-center gap-md">
          <span className="h-px flex-1 bg-border" />
          <span className="text-caption text-text-muted">{t('loginScreen.orContinueWith', 'Or continue with')}</span>
          <span className="h-px flex-1 bg-border" />
        </div>

        <div className="flex flex-col gap-sm">
          <button onClick={() => void run(signInGoogle)} disabled={busy} className="flex items-center justify-center gap-sm rounded-md border border-border bg-elevated px-md py-smd text-button font-medium text-text hover:bg-surface-inner disabled:opacity-60">
            <img src="/images/google.png" alt="" className="h-5 w-5" />
            {t('loginScreen.continueWithGoogle', 'Continue with Google')}
          </button>
          <button onClick={() => void run(signInApple)} disabled={busy} className="flex items-center justify-center gap-sm rounded-md border border-border bg-elevated px-md py-smd text-button font-medium text-text hover:bg-surface-inner disabled:opacity-60">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.4 12.7c0-2.5 2-3.7 2.1-3.8-1.2-1.7-3-1.9-3.6-2-1.5-.2-3 .9-3.8.9-.8 0-2-.9-3.3-.8-1.7 0-3.2 1-4.1 2.5-1.8 3-.5 7.6 1.3 10.1.9 1.2 1.9 2.6 3.2 2.6 1.3-.1 1.8-.8 3.3-.8s2 .8 3.3.8c1.4 0 2.2-1.3 3.1-2.5 1-1.4 1.4-2.8 1.4-2.9-.1 0-2.9-1.1-2.9-4.1zM14 5.2c.7-.8 1.2-2 1-3.2-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1.1 3.1 1.2.1 2.3-.6 3-1.4z" /></svg>
            {t('loginScreen.continueWithApple', 'Continue with Apple')}
          </button>
        </div>

        <p className="mt-lg text-center text-caption text-text-muted">
          {t('webapp.authNoSignup', 'New to Vore? Create your account in the mobile app, then sign in here.')}
        </p>
      </div>
    </div>
  )
}
